import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { ReturnTypeEnum } from "../../return";
import { CompensationMethod } from "./filters/compensation-method-filter";
import { ReturnStatus } from "./filters/return-status-filter";
import { SalesChannelValues } from "./filters/sales-channel-values";
import { ViewByEnum } from "./filters/view-by-toggle";

/**
 * Schema for return analytics input and output
 * This schema defines the data structure for returning analytics statistics
 */

// Input schema for return analytics statistics
export const ReturnAnalyticsStatisticsInputSchema = z.object({
  startDate: z
    .string()
    .describe("Start date for the analytics period (YYYY-MM-DD)"),
  endDate: z
    .string()
    .describe("End date for the analytics period (YYYY-MM-DD)"),
  types: z
    .array(createEnum(ReturnTypeEnum))
    .describe("Types of returns to include in the analytics"),
  salesChannels: z
    .array(z.enum(SalesChannelValues))
    .optional()
    .describe("Sales channels to filter by (optional)"),
  returnStatuses: z
    .array(createEnum(ReturnStatus))
    .optional()
    .describe("Return statuses to filter by (optional)"),
  compensationMethods: z
    .array(createEnum(CompensationMethod))
    .optional()
    .describe("Compensation methods to filter by (optional)"),
  viewBy: createEnum([
    ViewByEnum.ORDER_DATE,
    ViewByEnum.PROCESSED_DATE,
    ViewByEnum.SUBMITTED_DATE,
  ])
    .default(ViewByEnum.ORDER_DATE)
    .describe("Whether to view analytics by order date or processed date"),
});

// Output schema for return analytics statistics
export const ReturnAnalyticsStatisticsOutputSchema = z.object({
  keyMetrics: z
    .object({
      retainedRevenue: z
        .number()
        .describe(
          "The total value of completed products kept in your business via store credits and exchanges (i.e., not refunded). This doesn't include the value of non-returns or rejected returns. Formula: total value store credit + total value exchanges",
        ),
      refundedRevenue: z
        .number()
        .describe(
          "The total value of submitted products refunded to customers—money that has left your business. Formula: total value of refunds",
        ),
      retainedRevenueRatio: z
        .number()
        .describe(
          "Percentage of retained revenue compared to total products submitted. Formula: retained revenue / (retained revenue + refunded revenue)",
        ),
      refundedRevenueRatio: z
        .number()
        .describe(
          "Percentage of refunded revenue compared to total products submitted. Formula: refunded revenue / (retained revenue + refunded revenue)",
        ),
      upsellRevenue: z
        .number()
        .describe(
          "Total revenue from upsells during exchanges (e.g., a customer exchanges for a more expensive item). Calculations are based on the values of submitted returns",
        ),
      retainedReturnsCount: z
        .number()
        .describe(
          "Number of returns with any store credit or exchange component. Returns with mixed compensation (e.g., exchange + refund) are included.",
        ),
      refundedReturnsCount: z
        .number()
        .describe(
          "Number of returns with any refund component. Returns with mixed compensation (e.g., exchange + refund) are included.",
        ),
    })
    .describe(
      "Key financial metrics related to returns that help track revenue retention and loss",
    ),

  byOrderMetrics: z
    .object({
      totalOrders: z
        .number()
        .describe("Total number of customer orders in the period"),
      returnedOrders: z
        .number()
        .describe(
          "Number of unique orders with at least one return (only counted once per order)",
        ),
      returnedOrdersRate: z
        .number()
        .describe(
          "Percentage of returned orders from total orders. Formula: returned orders / total orders. This metric helps identify patterns in order-level returns",
        ),
      totalNonExchangeOrders: z
        .number()
        .describe("Total number of orders excluding exchange-only orders"),
      eligibleOrders: z
        .number()
        .describe("Number of orders eligible for Redo protection"),
      protectedOrders: z
        .number()
        .describe("Number of orders with Redo protection"),
    })
    .describe(
      "Metrics related to orders with returns, helping identify return patterns at the order level",
    ),

  byItemMetrics: z
    .object({
      totalItems: z
        .number()
        .describe("Total quantity of items that have been purchased"),
      returnedItems: z
        .number()
        .describe(
          "Total quantity of items that have been returned and processed",
        ),
      returnedItemsRate: z
        .number()
        .describe(
          "Percentage of items that are returned from total items ordered. Formula: returned items / total items purchased. This helps identify product-level return patterns",
        ),
    })
    .describe(
      "Metrics related to individual items returned, helping identify product-level return patterns",
    ),

  byRevenueMetrics: z
    .object({
      totalRevenue: z
        .number()
        .describe(
          "Total revenue generated. Calculated as quantity multiplied by price, excluding discounts or store credit used on orders",
        ),
      returnedRevenue: z
        .number()
        .describe(
          "Total value of accepted returned items that have been submitted & processed, including refunds, store credit, and exchanges",
        ),
      returnedRevenueRate: z
        .number()
        .describe(
          "The value of returns as a percentage of total revenue. Formula: returned revenue / total revenue. This helps understand the financial impact of returns",
        ),
      totalNetRevenue: z
        .number()
        .describe(
          "Net revenue generated. Calculated as actual net value plus shipping, tax, and duties from orders.",
        ),
      returnedNetRevenue: z
        .number()
        .describe(
          "Net value of accepted returned items, calculated as actual net return value plus tax and handling fees.",
        ),
      returnedNetRevenueRate: z
        .number()
        .describe(
          "Net returned revenue as a percentage of total net revenue. Formula: returned net revenue / total net revenue.",
        ),
    })
    .describe(
      "Metrics related to revenue impact of returns, helping understand the financial implications",
    ),

  returnsMetrics: z
    .object({
      submittedReturns: z
        .number()
        .describe(
          "Total number of returns submitted via customer submission or merchant-created. Doesn't count returns that have been reset",
        ),
      processedReturns: z
        .number()
        .describe("Total number of returns completed or rejected"),
      processedReturnsRate: z
        .number()
        .describe(
          "Percentage of processed returns out of submitted returns. Formula: processed returns / submitted returns. A higher rate indicates efficient return processing",
        ),
    })
    .describe(
      "Metrics related to return processing efficiency, helping track operational performance",
    ),
});

// Export type definitions for TypeScript
export type ReturnAnalyticsStatisticsInput = z.infer<
  typeof ReturnAnalyticsStatisticsInputSchema
>;
export type ReturnAnalyticsStatisticsOutput = z.infer<
  typeof ReturnAnalyticsStatisticsOutputSchema
>;

// Define the entire schema for RPC usage
export const returnAnalyticsStatistics = {
  name: "returnAnalyticsStatistics" as const,
  input: ReturnAnalyticsStatisticsInputSchema,
  output: ReturnAnalyticsStatisticsOutputSchema,
};

export const ReturnOperationsKeyStatsInputSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  types: z.array(createEnum(ReturnTypeEnum)),
  salesChannels: z
    .array(z.enum(SalesChannelValues))
    .optional()
    .describe("Sales channels to filter by (optional)"),
});

export const ReturnOperationsKeyStatsOutputSchema = z.object({
  numCreated: z
    .number()
    .describe(
      "The number of returns initiated by your team (not customer-initiated returns)",
    ),
  numApproved: z
    .number()
    .describe("The total number of returns approved by your team"),
  numProcessedComplete: z
    .number()
    .describe(
      "The total number of returns that have been fully processed. This includes returns completed regardless of the number of days taken to complete them",
    ),
  avgDaysToProcess: z
    .number()
    .describe(
      "The average time between the creation and completion of returns and claims (A completed return consists of returns that are fully processed or rejected, with all items accounted for).",
    ),
});

export const returnOperationsKeyStats = {
  name: "returnOperationsKeyStats" as const,
  input: ReturnOperationsKeyStatsInputSchema,
  output: ReturnOperationsKeyStatsOutputSchema,
};

export type ReturnOperationsKeyStatsInput = z.infer<
  typeof returnOperationsKeyStats.input
>;
export type ReturnOperationsKeyStatsOutput = z.infer<
  typeof returnOperationsKeyStats.output
>;

export const OperationsNewTotalsInputSchema = z.object({
  startDate: z
    .string()
    .describe("Start date for the analytics period (YYYY-MM-DD)"),
  endDate: z
    .string()
    .describe("End date for the analytics period (YYYY-MM-DD)"),
  types: z
    .array(createEnum(ReturnTypeEnum))
    .describe("Types of returns to include in the analytics"),
  salesChannels: z
    .array(z.enum(SalesChannelValues))
    .optional()
    .describe("Sales channels to filter by (optional)"),
});

export const OperationsNewTotalsOutputSchema = z
  .object({
    data: z.array(
      z.object({
        email: z.string().nullable().describe("Email of the operator"),
        userName: z.string().nullable().describe("Name of the operator"),
        numCreated: z
          .number()
          .describe("Number of returns created by the operator"),
        numApproved: z
          .number()
          .describe("Number of returns approved by the operator"),
        numProcessedComplete: z
          .number()
          .describe(
            "Number of returns processed to completion by the operator",
          ),
        numProcessedIncomplete: z
          .number()
          .describe(
            "Number of returns processed but not completed by the operator",
          ),
      }),
    ),
  })
  .describe("Returns and claims operations broken down by each employee");

// Define the entire schema for RPC usage
export const operationsNewTotals = {
  name: "getOperationsNewTotals" as const,
  input: OperationsNewTotalsInputSchema,
  output: OperationsNewTotalsOutputSchema,
};

export type OperationsNewTotalsInput = z.infer<
  typeof operationsNewTotals.input
>;
export type OperationsNewTotalsOutput = z.infer<
  typeof operationsNewTotals.output
>;
